import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { adminApi } from '../../services/api';
import SalesAgGridTable from '../../components/sales/SalesAgGridTable';
import RetellCallViewer from '../../components/sales/RetellCallViewer';
import VoiceAISalesCoach from '../../components/sales/shared/VoiceAISalesCoach';
import { formatPhoneDisplay, toE164 } from '../../utils/phoneUtils';

const RetellCallLogs = () => {
  const gridRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [calls, setCalls] = useState([]);
  const [agents, setAgents] = useState([]);
  const [phoneNumbers, setPhoneNumbers] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);

  // Side panel state for viewing call details
  const [selectedCallId, setSelectedCallId] = useState(null);
  const [showCallViewer, setShowCallViewer] = useState(false);

  // Filter states
  const [filters, setFilters] = useState({
    agent_id: '',
    call_status: '',
    call_type: '',
    direction: '',
    to_number: '',
    from_number: '',
    user_sentiment: '',
    call_successful: '',
    disconnection_reason: '',
    sort_order: 'descending',
    limit: 50
  });

  // Load agents and phone numbers on mount
  useEffect(() => {
    loadAgentsAndPhoneNumbers();
  }, []);

  const loadAgentsAndPhoneNumbers = async () => {
    try {
      const [agentsResponse, phoneNumbersResponse] = await Promise.all([
        adminApi.getRetellAgents(),
        adminApi.getRetellPhoneNumbers()
      ]);

      if (agentsResponse.data.success) {
        // Keep all agent versions, add display name with version FIRST to prevent truncation
        const agentsData = agentsResponse.data.data || [];
        const agentsWithVersion = agentsData.map(agent => ({
          ...agent,
          display_name: `${agent.agent_name || agent.agent_id} (v${agent.version}${agent.is_published ? ' ✓' : ''})`,
          full_name: agent.agent_name || agent.agent_id // Store for tooltip
        }));

        // Sort by agent name, then by version (descending - newest first)
        agentsWithVersion.sort((a, b) => {
          const nameA = a.agent_name || a.agent_id;
          const nameB = b.agent_name || b.agent_id;
          const nameCompare = nameA.localeCompare(nameB);
          if (nameCompare !== 0) return nameCompare;
          return b.version - a.version; // Descending version (newest first)
        });

        setAgents(agentsWithVersion);
      }
      if (phoneNumbersResponse.data.success) {
        setPhoneNumbers(phoneNumbersResponse.data.data || []);
      }
    } catch (err) {
      console.error('Failed to load agents and phone numbers:', err);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleToNumberBlur = () => {
    if (filters.to_number) {
      const e164 = toE164(filters.to_number);
      setFilters(prev => ({
        ...prev,
        to_number: formatPhoneDisplay(e164)
      }));
    }
  };

  const handleCoachSearchRequest = useCallback((coachFilters) => {
    // Clear all filters and apply only the coach's filters
    const cleanFilters = {
      agent_id: '',
      call_status: '',
      call_type: '',
      direction: '',
      to_number: '',
      from_number: '',
      user_sentiment: '',
      call_successful: '',
      disconnection_reason: '',
      sort_order: filters.sort_order, // Keep current sort order
      limit: coachFilters.limit || filters.limit, // Use coach's limit or keep current
      // Apply coach's filters
      ...coachFilters
    };

    // Update the filters state
    setFilters(cleanFilters);

    // Trigger search immediately with the new filters
    searchCallsWithFilters(cleanFilters);
  }, [filters]);

  const searchCallsWithFilters = async (filtersToUse) => {
    setLoading(true);
    setError(null);
    setHasSearched(true);

    try {
      // Build filter criteria (only include non-empty values)
      const filterData = {
        sort_order: filtersToUse.sort_order,
        limit: parseInt(filtersToUse.limit) || 50
      };

      // Add filter criteria only if values are provided
      const filter_criteria = {};

      if (filtersToUse.agent_id) filter_criteria.agent_id = [filtersToUse.agent_id];
      if (filtersToUse.call_status) filter_criteria.call_status = [filtersToUse.call_status];
      if (filtersToUse.call_type) filter_criteria.call_type = [filtersToUse.call_type];
      if (filtersToUse.direction) filter_criteria.direction = [filtersToUse.direction];
      if (filtersToUse.to_number) filter_criteria.to_number = [toE164(filtersToUse.to_number)];
      if (filtersToUse.from_number) filter_criteria.from_number = [filtersToUse.from_number];
      if (filtersToUse.user_sentiment) filter_criteria.user_sentiment = [filtersToUse.user_sentiment];
      if (filtersToUse.call_successful !== '') {
        filter_criteria.call_successful = [filtersToUse.call_successful === 'true'];
      }
      if (filtersToUse.disconnection_reason) filter_criteria.disconnection_reason = [filtersToUse.disconnection_reason];

      // Only add filter_criteria if there are filters
      if (Object.keys(filter_criteria).length > 0) {
        filterData.filter_criteria = filter_criteria;
      }

      const response = await adminApi.getRetellCalls(filterData);

      if (response.data.success) {
        setCalls(response.data.calls || []);
      } else {
        setError(response.data.error || 'Failed to fetch calls');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Error fetching calls');
      console.error('Error fetching calls:', err);
    } finally {
      setLoading(false);
    }
  };

  const searchCalls = async () => {
    // Use the current filters state
    await searchCallsWithFilters(filters);
  };

  const clearFilters = () => {
    setFilters({
      agent_id: '',
      call_status: '',
      call_type: '',
      direction: '',
      to_number: '',
      from_number: '',
      user_sentiment: '',
      call_successful: '',
      disconnection_reason: '',
      sort_order: 'descending',
      limit: 50
    });
    setCalls([]);
    setHasSearched(false);
  };

  const formatDuration = (seconds) => {
    if (!seconds) return 'N/A';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    // Retell AI returns timestamps in milliseconds, so no conversion needed
    return new Date(timestamp).toLocaleString();
  };

  const getStatusBadgeClass = (status) => {
    const statusMap = {
      'ended': 'success',
      'ongoing': 'primary',
      'error': 'danger',
      'registered': 'info'
    };
    return statusMap[status] || 'secondary';
  };

  const getSentimentBadgeClass = (sentiment) => {
    const sentimentMap = {
      'Positive': 'success',
      'Neutral': 'warning',
      'Negative': 'danger',
      'Unknown': 'secondary'
    };
    return sentimentMap[sentiment] || 'secondary';
  };

  const getDisconnectionReasonBadgeClass = (reason) => {
    const reasonMap = {
      // Normal endings
      'agent_hangup': 'info',
      'call_transfer': 'info',
      'user_hangup': 'success',
      // Dial issues
      'dial_busy': 'warning',
      'dial_failed': 'warning',
      'dial_no_answer': 'warning',
      // System limits
      'concurrency_limit_reached': 'secondary',
      // Errors
      'error_asr': 'danger',
      'error_llm_websocket_corrupt_payload': 'danger',
      'error_llm': 'danger',
      'error_frontend': 'danger'
    };
    return reasonMap[reason] || 'secondary';
  };

  const handleViewDetails = useCallback((callId) => {
    setSelectedCallId(callId);
    setShowCallViewer(true);
  }, []);

  const handleCloseCallViewer = useCallback(() => {
    setShowCallViewer(false);
    setSelectedCallId(null);
  }, []);

  // Handle row selection changes
  const onSelectionChanged = useCallback(() => {
    if (gridRef.current && gridRef.current.api) {
      const selectedNodes = gridRef.current.api.getSelectedNodes();
      const selectedData = selectedNodes.map(node => node.data);
      setSelectedRows(selectedData);
    }
  }, []);

  // AG Grid column definitions
  const columnDefs = useMemo(() => [
    {
      headerCheckboxSelection: true,
      checkboxSelection: true,
      pinned: 'left',
      width: 20,
      sortable: false,
      filter: false,
      suppressMovable: true,
      suppressMenu: true
    },
    {
      field: 'call_id',
      headerName: 'Call ID',
      width: 120,
      cellRenderer: (params) => {
        return params.value ? (
          <small className="font-monospace">{params.value}</small>
        ) : 'N/A';
      }
    },
    {
      field: 'retell_llm_dynamic_variables.job_title',
      headerName: 'Job Title',
      width: 180,
      valueGetter: (params) => params.data.retell_llm_dynamic_variables?.job_title || 'N/A'
    },
    {
      field: 'retell_llm_dynamic_variables.company_name',
      headerName: 'Company',
      width: 180,
      valueGetter: (params) => params.data.retell_llm_dynamic_variables?.company_name || 'N/A',
      cellRenderer: (params) => <strong>{params.value}</strong>
    },
    {
      field: 'retell_llm_dynamic_variables.prospect_name',
      headerName: 'Contact',
      width: 150,
      valueGetter: (params) => params.data.retell_llm_dynamic_variables?.prospect_name || 'N/A'
    },
    {
      field: 'agent_name',
      headerName: 'Agent',
      width: 150,
      cellRenderer: (params) => {
        return params.value ? (
          <small>{params.value}</small>
        ) : 'N/A';
      }
    },
    {
      field: 'direction',
      headerName: 'Direction',
      width: 110,
      cellRenderer: (params) => {
        const value = params.value;
        const badgeClass = value === 'inbound' ? 'info' : 'primary';
        return value ? (
          <span className={`badge bg-${badgeClass}`}>
            {value}
          </span>
        ) : 'N/A';
      }
    },
    {
      field: 'from_number',
      headerName: 'From',
      width: 150,
      cellRenderer: (params) => <small>{formatPhoneDisplay(params.value) || 'N/A'}</small>
    },
    {
      field: 'to_number',
      headerName: 'To',
      width: 150,
      cellRenderer: (params) => <small>{formatPhoneDisplay(params.value) || 'N/A'}</small>
    },
    {
      field: 'call_status',
      headerName: 'Status',
      width: 110,
      cellRenderer: (params) => {
        const badgeClass = getStatusBadgeClass(params.value);
        return (
          <span className={`badge bg-${badgeClass}`}>
            {params.value || 'N/A'}
          </span>
        );
      }
    },
    {
      field: 'disconnection_reason',
      headerName: 'Disconnection Reason',
      width: 200,
      cellRenderer: (params) => {
        if (!params.value) return <span className="text-muted small">N/A</span>;
        // Format the value for display (convert snake_case to Title Case)
        const formatted = params.value
          .split('_')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
        const badgeClass = getDisconnectionReasonBadgeClass(params.value);
        return (
          <span className={`badge bg-${badgeClass}`} style={{ fontSize: '0.75rem' }}>
            {formatted}
          </span>
        );
      }
    },
    {
      field: 'duration_ms',
      headerName: 'Duration',
      width: 110,
      valueFormatter: (params) => {
        if (!params.value) return 'N/A';
        const seconds = Math.floor(params.value / 1000);
        return formatDuration(seconds);
      }
    },
    {
      field: 'call_analysis.user_sentiment',
      headerName: 'Sentiment',
      width: 120,
      valueGetter: (params) => params.data.call_analysis?.user_sentiment || 'Unknown',
      cellRenderer: (params) => {
        const badgeClass = getSentimentBadgeClass(params.value);
        return (
          <span className={`badge bg-${badgeClass}`}>
            {params.value}
          </span>
        );
      }
    },
    {
      field: 'call_analysis.call_successful',
      headerName: 'Success',
      width: 90,
      valueGetter: (params) => params.data.call_analysis?.call_successful,
      cellRenderer: (params) => {
        return params.value ? (
          <i className="fas fa-check-circle text-success"></i>
        ) : (
          <i className="fas fa-times-circle text-danger"></i>
        );
      }
    },
    {
      field: 'start_timestamp',
      headerName: 'Start Time',
      width: 180,
      valueFormatter: (params) => formatDate(params.value)
    },
    {
      headerName: 'Actions',
      width: 110,
      cellRenderer: (params) => {
        return (
          <button
            className="btn btn-sm btn-outline-primary"
            onClick={() => handleViewDetails(params.data.call_id)}
            title="View Details"
          >
            <i className="fas fa-eye me-1"></i>
            Details
          </button>
        );
      },
      sortable: false,
      filter: false,
      pinned: 'right'
    }
  ], [handleViewDetails]);

  const defaultColDef = useMemo(() => ({
    sortable: true,
    filter: true,
    resizable: true,
    floatingFilter: false
  }), []);

  return (
    <>
      {/* Retell Call Viewer Side Panel */}
      <RetellCallViewer
        isOpen={showCallViewer}
        onClose={handleCloseCallViewer}
        callId={selectedCallId}
      />

      {/* Page Title */}
      <div className="page-title-box d-flex align-items-center justify-content-between mb-3">
        <div className="col-8">
          <h4 className="mb-0 font-size-18" style={{ background: 'linear-gradient(135deg, #530e7bff 0%, #373968ff 50%, #4b4fa2ff 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', fontWeight: 600 }}>Voice AI Coach</h4>
          <p className="text-muted mt-2">Search previous voice AI calls, replay interactions, improve performance</p>
        </div>
        <div className="col-4 d-flex justify-content-end">
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb m-0">
              <li className="breadcrumb-item"><a href="/admin/sales">Sales</a></li>
              <li className="breadcrumb-item active">Voice AI Coach</li>
            </ol>
          </nav>
        </div>
      </div>

      {/* Sales Coach Assistant - Fixed position bottom right */}
      <div style={{position: 'fixed', bottom: 20, right: 20, zIndex: 9999, width: '400px', maxWidth: '90vw' }}>
        <VoiceAISalesCoach
          onSearchRequest={handleCoachSearchRequest}
          agents={agents}
        />
      </div>

      {/* Error Alert */}
      {error && (
        <div className="alert alert-danger">
          <i className="fas fa-exclamation-triangle me-2"></i>
          {error}
        </div>
      )}

      {/* Filters Card */}
      <div className="card" style={{overflowY:'scroll'}}>
        <div className="mb-3">
          <div className='d-flex gap-2 flex-wrap mb-2 p-3'>
            <select
              className="form-select form-select-sm"
              value={filters.agent_id}
              onChange={(e) => handleFilterChange('agent_id', e.target.value)}
              title="Select agent. Version and published status shown first to prevent truncation"
              style={{ width: 'auto', fontSize: '0.85rem' }}
            >
              <option value="">All Agents</option>
              {agents.map(agent => (
                <option
                  key={`${agent.agent_id}_v${agent.version}`}
                  value={agent.agent_id}
                  title={agent.full_name}
                >
                  {agent.display_name}
                </option>
              ))}
            </select>
            <select
              className="form-select form-select-sm"
              value={filters.call_status}
              onChange={(e) => handleFilterChange('call_status', e.target.value)}
              style={{ width: 'auto' }}
            >
              <option value="">All Status</option>
              <option value="ended">Ended</option>
              <option value="error">Error</option>
              <option value="not_connected">Not Connected</option>
              <option value="ongoing">Ongoing</option>
            </select>
            <select
              className="form-select form-select-sm"
              value={filters.direction}
              onChange={(e) => handleFilterChange('direction', e.target.value)}
              style={{ width: 'auto' }}
            >
              <option value="">All Directions</option>
              <option value="inbound">Inbound</option>
              <option value="outbound">Outbound</option>
            </select>
            <select
              className="form-select form-select-sm"
              value={filters.from_number}
              onChange={(e) => handleFilterChange('from_number', e.target.value)}
              style={{ width: 'auto' }}
            >
              <option value="">All From Numbers</option>
              {phoneNumbers.map(phone => (
                <option key={phone.phone_number} value={phone.phone_number}>
                  {phone.nickname || phone.phone_number}
                </option>
              ))}
            </select>
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="To Number"
              value={filters.to_number}
              onChange={(e) => handleFilterChange('to_number', e.target.value)}
              onBlur={handleToNumberBlur}
              style={{ width: '140px' }}
            />
            <select
              className="form-select form-select-sm"
              value={filters.user_sentiment}
              onChange={(e) => handleFilterChange('user_sentiment', e.target.value)}
              style={{ width: 'auto' }}
            >
              <option value="">All Sentiments</option>
              <option value="Positive">Positive</option>
              <option value="Neutral">Neutral</option>
              <option value="Negative">Negative</option>
              <option value="Unknown">Unknown</option>
            </select>
            <select
              className="form-select form-select-sm"
              value={filters.call_successful}
              onChange={(e) => handleFilterChange('call_successful', e.target.value)}
              style={{ width: 'auto' }}
            >
              <option value="">All Success</option>
              <option value="true">Successful</option>
              <option value="false">Not Successful</option>
            </select>
            <select
              className="form-select form-select-sm"
              value={filters.disconnection_reason}
              onChange={(e) => handleFilterChange('disconnection_reason', e.target.value)}
              style={{ width: 'auto' }}
            >
              <option value="">All Disconnection Reasons</option>
              <option value="agent_hangup">Agent Hangup</option>
              <option value="call_transfer">Call Transfer</option>
              <option value="concurrency_limit_reached">Concurrency Limit</option>
              <option value="dial_busy">Dial Busy</option>
              <option value="dial_failed">Dial Failed</option>
              <option value="dial_no_answer">Dial No Answer</option>
              <option value="error_asr">Error ASR</option>
              <option value="error_llm_websocket_corrupt_payload">Error LLM</option>
            </select>
            <select
              className="form-select form-select-sm"
              value={filters.limit}
              onChange={(e) => handleFilterChange('limit', e.target.value)}
              style={{ width: 'auto' }}
            >
              <option value="25">Limit 25</option>
              <option value="50">Limit 50</option>
              <option value="100">Limit 100</option>
              <option value="250">Limit 250</option>
              <option value="500">Limit 500</option>
              <option value="1000">Limit 1000</option>
            </select>
            <select
              className="form-select form-select-sm"
              value={filters.sort_order}
              onChange={(e) => handleFilterChange('sort_order', e.target.value)}
              style={{ width: 'auto' }}
            >
              <option value="descending">Newest First</option>
              <option value="ascending">Oldest First</option>
            </select>
              <button
                className="btn btn-sm btn-primary"
                onClick={searchCalls}
                disabled={loading}
              >
                <i className="fas fa-search me-1"></i>
                {loading ? 'Searching...' : 'Search'}
              </button>
              <button
                className="btn btn-sm btn-secondary"
                onClick={clearFilters}
                disabled={loading}
                title="Clear all filters"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>

      {/* Results Grid */}
      {calls.length > 0 && (
                    <>
                <div className="d-flex align-items-center justify-content-between p-3">
                  <div className="d-flex align-items-center gap-2 mt-4">
                    <h5 className="text-large">
                      Search Results (Showing {calls.length} calls)
                    </h5>
                    {selectedRows.length > 0 && (
                      <div className="btn-group btn-group-sm">
                        <button
                          className="btn btn-outline-secondary"
                          onClick={() => gridRef.current?.api?.exportDataAsCsv({
                            onlySelected: true,
                            fileName: `retell-call-logs-${new Date().toISOString().slice(0, 10)}.csv`,
                            columnKeys: ['call_id', 'retell_llm_dynamic_variables.company_name', 'retell_llm_dynamic_variables.job_title', 'retell_llm_dynamic_variables.prospect_name', 'agent_name', 'direction', 'from_number', 'to_number', 'call_status', 'disconnection_reason', 'duration_ms', 'call_analysis.user_sentiment', 'call_analysis.call_successful', 'start_timestamp'],
                            processHeaderCallback: (params) => params.column.getColDef().headerName || params.column.getColDef().field || params.column.getColId(),
                          })}
                          disabled={selectedRows.length === 0}
                          title="Export selected rows to CSV"
                        >
                          <i className="fas fa-file-csv me-1"></i>
                          Export CSV ({selectedRows.length})
                        </button>
                        <button
                          className="btn btn-outline-secondary"
                          onClick={() => gridRef.current?.api?.exportDataAsExcel({
                            onlySelected: true,
                            fileName: `retell-call-logs-${new Date().toISOString().slice(0, 10)}.xlsx`,
                            columnKeys: ['call_id', 'retell_llm_dynamic_variables.company_name', 'retell_llm_dynamic_variables.job_title', 'retell_llm_dynamic_variables.prospect_name', 'agent_name', 'direction', 'from_number', 'to_number', 'call_status', 'disconnection_reason', 'duration_ms', 'call_analysis.user_sentiment', 'call_analysis.call_successful', 'start_timestamp'],
                          })}
                          disabled={selectedRows.length === 0}
                          title="Export selected rows to Excel"
                        >
                          <i className="fas fa-file-excel me-1"></i>
                          Export Excel ({selectedRows.length})
                        </button>
                      </div>
                    )}
                  {selectedRows.length > 0 && (
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => gridRef.current?.api?.deselectAll()}
                      title="Clear selection"
                    >
                      <i className="fas fa-times me-1"></i>
                      Clear Selection
                    </button>
                  )}
                  </div>
                </div>
                <SalesAgGridTable
                  ref={gridRef}
                  rowData={calls}
                  columnDefs={columnDefs}
                  defaultColDef={defaultColDef}
                  domLayout="autoHeight"
                  pagination={true}
                  paginationPageSize={50}
                  paginationPageSizeSelector={[10, 25, 50, 100]}
                  rowSelection="multiple"
                  suppressRowClickSelection={true}
                  animateRows={true}
                  getRowId={(params) => params.data.call_id}
                  onSelectionChanged={onSelectionChanged}
                />
        </>
      )}

      {/* No Results Message */}
      {!error && calls.length === 0 && !loading && (
            <div className="text-center text-muted my-5" role="alert">
              <i className="fas fa-info-circle me-2"></i>
              {!hasSearched ? (<span>Start searching for calls using the filters above</span>):(<span>No calls found. Adjust your filters and search again.</span>)}
              </div>
      )}
            </div>
          </div>
    </>
  );
};

export default RetellCallLogs;